// src/services/db.js
import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  onSnapshot,
  runTransaction,
  writeBatch,
  serverTimestamp,
  increment,
} from "firebase/firestore";
import { db, COL } from "../firebase";

/* ------------------------------------------------------------------ */
/*  helpers                                                            */
/* ------------------------------------------------------------------ */

const snapToArray = (snap) => snap.docs.map((d) => ({ id: d.id, ...d.data() }));

export const buildCodes = (codeNumber, hasMb) => {
  const n = String(codeNumber ?? "").trim();
  if (!n) return { pmuCode: "", mbCode: null };
  const padded = n.padStart(2, "0");
  return {
    pmuCode: `LaBina-CL${padded}`,
    mbCode: hasMb ? `LaBina-ML${padded}` : null,
  };
};

/**
 * Either name may be blank — whichever exists is the product's name.
 * Never assume nameSq is present.
 */
export const displayName = (p) =>
  p?.nameSq?.trim() || p?.nameDe?.trim() || p?.pmuCode || "";

/** Only returns a secondary name when both languages were actually filled. */
export const secondaryName = (p) =>
  p?.nameSq?.trim() && p?.nameDe?.trim() ? p.nameDe.trim() : "";

export const slugify = (str) =>
  String(str || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

/* ------------------------------------------------------------------ */
/*  categories                                                         */
/* ------------------------------------------------------------------ */

export const getCategories = async () => {
  const snap = await getDocs(
    query(collection(db, COL.categories), orderBy("order", "asc")),
  );
  return snapToArray(snap);
};

export class DuplicateCategoryError extends Error {
  constructor(nameSq) {
    super("DUPLICATE_CATEGORY");
    this.name = "DuplicateCategoryError";
    this.nameSq = nameSq;
  }
}

/**
 * The doc id is derived from the name so products keep matching even if
 * the display name is edited later. Returns the id so the caller can
 * select the category immediately.
 */
export const addCategory = async ({ nameSq, order = 99 }) => {
  const id = slugify(nameSq);
  if (!id) throw new Error("EMPTY_CATEGORY_NAME");

  const ref = doc(db, COL.categories, id);
  const existing = await getDoc(ref);
  if (existing.exists()) throw new DuplicateCategoryError(nameSq);

  await setDoc(ref, {
    nameSq: nameSq.trim(),
    order,
    createdAt: serverTimestamp(),
  });
  return id;
};

/** Turns a raw Firebase error into something an admin can act on. */
export const explainError = (err) => {
  if (err instanceof DuplicateCategoryError)
    return `"${err.nameSq}" ekziston tashmë.`;
  switch (err?.code) {
    case "permission-denied":
      return "Nuk keni leje për këtë veprim. Kontrolloni rregullat e Firestore (Rules) dhe që të jeni të kyçur.";
    case "unavailable":
    case "failed-precondition":
      return "Nuk ka lidhje me bazën e të dhënave. Kontrolloni internetin.";
    case "unauthenticated":
      return "Sesioni skadoi. Kyçuni përsëri.";
    default:
      return err?.message ? `Gabim: ${err.message}` : "Diçka shkoi keq.";
  }
};

export const updateCategory = (id, data) =>
  updateDoc(doc(db, COL.categories, id), data);

export const deleteCategory = (id) => deleteDoc(doc(db, COL.categories, id));

/* ------------------------------------------------------------------ */
/*  products                                                           */
/* ------------------------------------------------------------------ */

export const getProducts = async ({ categoryId } = {}) => {
  const base = collection(db, COL.products);
  // Codes are optional now, so ordering happens client-side —
  // an orderBy on a missing field would silently drop those docs.
  const q = categoryId
    ? query(base, where("categoryId", "==", categoryId))
    : base;
  return snapToArray(await getDocs(q));
};

export const getProduct = async (id) => {
  const snap = await getDoc(doc(db, COL.products, id));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
};

export const listenProducts = (cb) =>
  onSnapshot(collection(db, COL.products), (snap) => cb(snapToArray(snap)));

const normaliseProduct = (data) => {
  const { pmuCode, mbCode } = buildCodes(data.codeNumber, data.hasMb);
  return {
    codeNumber: String(data.codeNumber || "").trim(),
    pmuCode,
    mbCode,
    hasMb: !!data.hasMb,
    nameDe: data.nameDe?.trim() || "",
    nameSq: data.nameSq?.trim() || "",
    slug: slugify(`${data.nameSq || data.nameDe || ""}-${data.codeNumber}`),
    description: data.description?.trim() || "",
    hex: data.hex || null,
    categoryId: data.categoryId || "",
    onePigment: !!data.onePigment,
    isSample: !!data.isSample,
    price: Number(data.price) || 0,
    stock: Math.max(0, Number(data.stock) || 0),
    lowStockAt: Number(data.lowStockAt) || 3,
    images: data.images || [],
    active: data.active !== false,
  };
};

export const addProduct = (data) =>
  addDoc(collection(db, COL.products), {
    ...normaliseProduct(data),
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

export const updateProduct = (id, data) =>
  updateDoc(doc(db, COL.products, id), {
    ...normaliseProduct(data),
    updatedAt: serverTimestamp(),
  });

export const deleteProduct = (id) => deleteDoc(doc(db, COL.products, id));

/** Adjust stock by a signed amount, e.g. +12 on a restock. */
export const adjustStock = (id, delta) =>
  updateDoc(doc(db, COL.products, id), {
    stock: increment(delta),
    updatedAt: serverTimestamp(),
  });

/** Bulk import. Firestore caps a batch at 500 writes, so chunk it. */
export const bulkAddProducts = async (rows) => {
  const chunks = [];
  for (let i = 0; i < rows.length; i += 400)
    chunks.push(rows.slice(i, i + 400));

  let written = 0;
  for (const chunk of chunks) {
    const batch = writeBatch(db);
    chunk.forEach((row) => {
      const ref = doc(collection(db, COL.products));
      batch.set(ref, {
        ...normaliseProduct(row),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
    });
    await batch.commit();
    written += chunk.length;
  }
  return written;
};

/* ------------------------------------------------------------------ */
/*  orders + stock                                                     */
/* ------------------------------------------------------------------ */

export class StockError extends Error {
  constructor(items) {
    super("INSUFFICIENT_STOCK");
    this.name = "StockError";
    this.items = items; // [{ id, nameSq, requested, available }]
  }
}

/**
 * Places an order and decrements stock atomically.
 *
 * Every read happens before every write — Firestore rejects a transaction
 * that reads after writing. If any line is short, nothing is written at all
 * and a StockError comes back listing exactly which items failed.
 */
export const placeOrder = async ({ customer, items, note = "" }) => {
  if (!items?.length) throw new Error("EMPTY_CART");

  const orderRef = doc(collection(db, COL.orders));

  await runTransaction(db, async (tx) => {
    // 1. read every product in the cart
    const snaps = await Promise.all(
      items.map((i) => tx.get(doc(db, COL.products, i.id))),
    );

    // 2. verify stock before touching anything
    const short = [];
    const lines = [];

    snaps.forEach((snap, idx) => {
      const item = items[idx];
      if (!snap.exists()) {
        short.push({
          id: item.id,
          nameSq: item.nameSq,
          requested: item.qty,
          available: 0,
        });
        return;
      }
      const p = snap.data();
      if (p.isSample) {
        short.push({
          id: item.id,
          nameSq: displayName(p),
          requested: item.qty,
          available: 0,
        });
        return;
      }
      const available = Number(p.stock) || 0;
      if (available < item.qty) {
        short.push({
          id: item.id,
          nameSq: displayName(p),
          requested: item.qty,
          available,
        });
        return;
      }
      lines.push({
        id: item.id,
        qty: item.qty,
        nameSq: displayName(p),
        pmuCode: p.pmuCode,
        hex: p.hex,
        price: Number(p.price) || 0,
        image: p.images?.[0] || null,
      });
    });

    if (short.length) throw new StockError(short);

    // 3. writes
    items.forEach((item) => {
      tx.update(doc(db, COL.products, item.id), {
        stock: increment(-item.qty),
        updatedAt: serverTimestamp(),
      });
    });

    const total = lines.reduce((sum, l) => sum + l.price * l.qty, 0);

    tx.set(orderRef, {
      customer,
      items: lines,
      total,
      note,
      status: "new", // new | confirmed | shipped | delivered | cancelled
      stockReturned: false,
      createdAt: serverTimestamp(),
    });
  });

  return orderRef.id;
};

/* ------------------------------------------------------------------ */
/*  sample requests                                                    */
/* ------------------------------------------------------------------ */

/**
 * Samples are given away, so they can't go through checkout — anyone would
 * take one. A request is captured instead and the client approves it by hand.
 * Stock is never touched here; nothing ships until a human says so.
 */
export const createSampleRequest = async ({ product, contact, note = "" }) => {
  const ref = await addDoc(collection(db, COL.sampleRequests), {
    product: {
      id: product.id,
      nameSq: displayName(product),
      pmuCode: product.pmuCode || "",
      hex: product.hex || "",
      image: product.images?.[0] || null,
    },
    contact,
    note,
    status: "new", // new | approved | sent | rejected
    createdAt: serverTimestamp(),
  });
  return ref.id;
};

export const listenSampleRequests = (cb) =>
  onSnapshot(
    query(collection(db, COL.sampleRequests), orderBy("createdAt", "desc")),
    (snap) => cb(snapToArray(snap)),
  );

export const updateSampleRequestStatus = (id, status) =>
  updateDoc(doc(db, COL.sampleRequests, id), {
    status,
    updatedAt: serverTimestamp(),
  });

export const deleteSampleRequest = (id) =>
  deleteDoc(doc(db, COL.sampleRequests, id));

export const listenOrders = (cb) =>
  onSnapshot(
    query(collection(db, COL.orders), orderBy("createdAt", "desc")),
    (snap) => cb(snapToArray(snap)),
  );

/**
 * Changes an order's status. Cancelling returns the stock to the shelf,
 * guarded by stockReturned so a double-cancel can't inflate inventory.
 */
export const updateOrderStatus = async (orderId, status) => {
  const orderRef = doc(db, COL.orders, orderId);

  await runTransaction(db, async (tx) => {
    const snap = await tx.get(orderRef);
    if (!snap.exists()) throw new Error("ORDER_NOT_FOUND");

    const order = snap.data();
    const cancelling = status === "cancelled" && !order.stockReturned;
    const reopening =
      order.status === "cancelled" &&
      status !== "cancelled" &&
      order.stockReturned;

    if (cancelling || reopening) {
      const sign = cancelling ? 1 : -1;
      order.items.forEach((line) => {
        tx.update(doc(db, COL.products, line.id), {
          stock: increment(sign * line.qty),
          updatedAt: serverTimestamp(),
        });
      });
    }

    tx.update(orderRef, {
      status,
      stockReturned: cancelling
        ? true
        : reopening
          ? false
          : order.stockReturned,
      updatedAt: serverTimestamp(),
    });
  });
};

export const deleteOrder = (id) => deleteDoc(doc(db, COL.orders, id));
